import { supabase } from './supabase';
import type { Session, User } from '@supabase/supabase-js';

export type AuthSession = Session;
export type AuthUser = User;

/** Hard-required suffix for any signup or signin attempt. */
export const REQUIRED_EMAIL_DOMAIN = '@liteline.com';

export function isLitelineEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(REQUIRED_EMAIL_DOMAIN);
}

export async function signInWithPassword(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error) throw error;
  return data;
}

export async function signUpWithPassword(
  email: string,
  password: string,
  displayName?: string,
) {
  const cleaned = email.trim().toLowerCase();
  if (!isLitelineEmail(cleaned)) {
    throw new Error(`Sign-up is restricted to ${REQUIRED_EMAIL_DOMAIN} addresses.`);
  }
  const { data, error } = await supabase.auth.signUp({
    email: cleaned,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/login`,
      data: displayName ? { display_name: displayName } : undefined,
    },
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function sendPasswordReset(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(
    email.trim().toLowerCase(),
    { redirectTo: `${window.location.origin}/reset-password` },
  );
  if (error) throw error;
}

export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function getSession(): Promise<AuthSession | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}
